import { FireCelebrationHost } from '../../components/FireLifecycle';
import { useEffect, useRef, useState } from 'react';
import { Platform, Pressable, Text, View } from 'react-native';
import { Tabs, usePathname, useRouter } from 'expo-router';
import type { Href } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { isOnboardingComplete } from '@perdiem/trpc/onboarding';
import { SafeAreaView } from 'react-native-safe-area-context';
import { trpc } from '../../lib/trpc';
import { useColors } from '../../lib/colors';
import {
  DemoModeBanner,
  ErrorState,
  FireRecalcBanner,
  LoadingSpinner,
  MobileAppInterstitial,
} from '../../components';
import { WebSidebar } from '../../components/WebSidebar';
import { MobileTopBar } from '../../components/MobileTopBar';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import {
  clearSyntheticSessionForRedirect,
  recoverFromAuthError,
  supabase,
} from '../../lib/supabase';
import {
  trackAppBootstrapReady,
  trackAppScreenViewed,
  trackAuthSignedOut,
} from '../../lib/product-events';

const ACTION_BADGE_STYLE = {
  minWidth: 18,
  height: 18,
  borderRadius: 9,
  backgroundColor: '#fbbf24',
  color: '#78350f',
  fontSize: 10,
  fontWeight: '700' as const,
};
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1']);

function buildSignupPath(target: 'web' | 'router'): string {
  const params = new URLSearchParams({
    mode: 'signup',
  });
  const hostname = typeof globalThis.location?.hostname === 'string'
    ? globalThis.location.hostname
    : '';
  if (Platform.OS === 'web' && LOOPBACK_HOSTS.has(hostname)) {
    params.set('regular', '1');
  }

  const path = target === 'web' ? '/login' : '/(auth)/login';
  return `${path}?${params.toString()}`;
}

function formatActionCount(count: number) {
  return count > 99 ? '99+' : String(count);
}

function getTabActionBadgeOptions(count: number) {
  if (count <= 0) return {};

  return {
    tabBarBadge: formatActionCount(count),
    tabBarBadgeStyle: ACTION_BADGE_STYLE,
  };
}

function SubscriptionExpiringBanner({
  daysUntilExpiration,
  kind,
  onSubscribe,
}: {
  daysUntilExpiration: number | null;
  kind: 'promotional_trial' | 'canceled_subscription';
  onSubscribe: () => void;
}) {
  const dayLabel = daysUntilExpiration === 1 ? '1 day' : `${daysUntilExpiration ?? 0} days`;
  const message = kind === 'canceled_subscription'
    ? `Your subscription is canceled and access ends in ${dayLabel}. Renew to keep using Per Diem.`
    : `Your promotional trial ends in ${dayLabel}. Subscribe to keep access to Per Diem, including live account sync.`;

  return (
    <View className="border-b border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900 dark:bg-amber-950">
      <View className="mx-auto w-full max-w-[960px] flex-row items-center justify-between gap-3">
        <Text className="flex-1 text-sm font-medium text-amber-900 dark:text-amber-100">
          {message}
        </Text>
        <Pressable
          onPress={onSubscribe}
          className="rounded-lg bg-amber-600 px-3 py-2"
        >
          <Text className="text-xs font-semibold text-white">
            {kind === 'canceled_subscription' ? 'Review' : 'Subscribe'}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

/**
 * Authenticated app shell.
 *
 * On mount, checks household.onboardingStep and redirects unfinished households.
 *
 * Desktop web (>= 768px): Sidebar + content, tab bar hidden
 * Mobile / narrow web: Bottom tab bar
 */
export default function AppLayout() {
  const router = useRouter();
  const utils = trpc.useUtils();
  const isDesktop = useIsDesktop();
  const c = useColors();
  const bootstrapStartedAtRef = useRef(Date.now());
  const bootstrapReportedRef = useRef(false);
  const bootstrapQ = trpc.bootstrap.get.useQuery(undefined, {
    staleTime: 120_000,
    retry: (failureCount, error) => {
      if (error.data?.code === 'UNAUTHORIZED') {
        return failureCount < 3;
      }

      return failureCount < 1;
    },
    retryDelay: (attemptIndex) => Math.min(300 * attemptIndex, 900),
  });
  const householdQ = {
    data: bootstrapQ.data?.household,
    isLoading: bootstrapQ.isLoading,
    error: bootstrapQ.error,
    refetch: bootstrapQ.refetch,
  };
  const household = householdQ.data;
  const isDemo = household?.isDemo ?? false;
  const pathname = usePathname();
  const isSettingsRoute = pathname?.includes('/settings') ?? false;
  const reportedScreenRef = useRef<string | null>(null);
  const [demoBannerDismissed, setDemoBannerDismissed] = useState(false);
  const shellActionQueriesEnabled = Boolean(
    household && isOnboardingComplete(household.onboardingStep),
  );
  const subscriptionQ = {
    data: bootstrapQ.data?.subscription,
    isLoading: bootstrapQ.isLoading,
  };
  const reviewCountQ = trpc.transactions.reviewCount.useQuery(undefined, {
    enabled: shellActionQueriesEnabled,
    staleTime: 300_000,
  });
  const planHealthQ = trpc.planHealth.scan.useQuery(undefined, {
    enabled: shellActionQueriesEnabled,
    staleTime: 300_000,
    refetchOnWindowFocus: false,
  });
  const ledgerActionCount = reviewCountQ.data?.count ?? 0;
  const firePlanActionCount = planHealthQ.data?.issues.length ?? 0;
  const shouldRestrictAppAccess = Boolean(
    !isDemo
      && subscriptionQ.data
      && !subscriptionQ.data.isActive,
  );
  const visibleLedgerActionCount = shouldRestrictAppAccess ? 0 : ledgerActionCount;
  const visibleFirePlanActionCount = shouldRestrictAppAccess ? 0 : firePlanActionCount;
  const expiringSubscriptionBannerKind =
    subscriptionQ.data?.accessKind === 'trial_promo'
      ? 'promotional_trial'
      : subscriptionQ.data?.accessKind === 'subscription' && subscriptionQ.data.subscriptionIsCanceled
        ? 'canceled_subscription'
        : null;
  const shouldShowSubscriptionExpiringBanner = Boolean(
    !isDemo
      && subscriptionQ.data?.isActive
      && expiringSubscriptionBannerKind
      && subscriptionQ.data.daysUntilExpiration !== null
      && subscriptionQ.data.daysUntilExpiration <= 7
      && !isSettingsRoute,
  );
  const ledgerActionHref = {
    pathname: '/(app)/transactions',
    params: { needsReview: '1' },
  } as Href;

  // Register device for push notifications on app launch
  usePushNotifications();

  useEffect(() => {
    if (householdQ.isLoading) return;
    if (household && !isOnboardingComplete(household.onboardingStep)) {
      router.replace('/(onboarding)');
    }
  }, [household, householdQ.isLoading]);

  useEffect(() => {
    if (!bootstrapQ.data || bootstrapReportedRef.current) return;
    bootstrapReportedRef.current = true;
    trackAppBootstrapReady({
      route: pathname,
      durationMs: Date.now() - bootstrapStartedAtRef.current,
      metadata: {
        isDemo,
      },
    });
  }, [bootstrapQ.data, isDemo, pathname]);

  useEffect(() => {
    if (!bootstrapQ.data) return;
    utils.households.get.setData(undefined, bootstrapQ.data.household);
    if (bootstrapQ.data.subscription) {
      utils.subscription.getStatus.setData(undefined, bootstrapQ.data.subscription);
    }
    utils.legal.status.setData(undefined, bootstrapQ.data.legal);
  }, [bootstrapQ.data, utils]);

  useEffect(() => {
    if (
      !shellActionQueriesEnabled
      || shouldRestrictAppAccess
      || bootstrapQ.isFetching
    ) {
      return;
    }

    const timeout = setTimeout(() => {
      void Promise.allSettled([
        utils.accounts.list.prefetch(),
        utils.assets.list.prefetch(),
        utils.liabilities.list.prefetch(),
        utils.budget.getEntries.prefetch(),
        utils.budget.averages.prefetch(),
        utils.budget.currentMonthActuals.prefetch(),
        utils.budget.currentYearActuals.prefetch(),
        utils.budget.perDiem.prefetch(),
        utils.fire.getPlan.prefetch(),
        utils.profiles.list.prefetch(),
      ]);
    }, 800);

    return () => clearTimeout(timeout);
  }, [
    bootstrapQ.isFetching,
    shellActionQueriesEnabled,
    shouldRestrictAppAccess,
    utils,
  ]);

  useEffect(() => {
    if (
      householdQ.isLoading ||
      !household ||
      !isOnboardingComplete(household.onboardingStep)
    ) {
      return;
    }

    const reportKey = pathname;
    if (!reportKey || reportedScreenRef.current === reportKey) {
      return;
    }

    reportedScreenRef.current = reportKey;

    const screenName = pathname.split('/').filter(Boolean).at(-1) ?? 'index';
    trackAppScreenViewed({
      route: pathname,
      screenName,
      metadata: {
        currentGroup: 'app',
        isDemo,
      },
    });
  }, [household, householdQ.isLoading, isDemo, pathname]);

  useEffect(() => {
    if (!isDemo) {
      setDemoBannerDismissed(false);
    }
  }, [isDemo]);

  if (householdQ.isLoading) {
    return <LoadingSpinner message="Loading your workspace..." />;
  }

  if (householdQ.error) {
    const isAuthError = householdQ.error.data?.code === 'UNAUTHORIZED';
    if (isAuthError) {
      return (
        <ErrorState
          message="Your session couldn't be verified. Sign in again to continue."
          error={householdQ.error}
          screen="app-shell"
          metadata={{ failedQueries: 'bootstrap.get', authError: true }}
          retryLabel="Sign In Again"
          onRetry={() => {
            void (async () => {
              await recoverFromAuthError();

              if (typeof window !== 'undefined') {
                window.location.replace('/login');
                return;
              }

              router.replace('/(auth)/login');
            })();
          }}
        />
      );
    }

    return (
      <ErrorState
        message="Could not load your workspace."
        error={householdQ.error}
        screen="app-shell"
        metadata={{ failedQueries: 'bootstrap.get' }}
        onRetry={() => {
          void householdQ.refetch();
        }}
      />
    );
  }

  if (!household) {
    return (
      <ErrorState
        message="We couldn't find your workspace."
        screen="app-shell"
        metadata={{ failedQueries: 'bootstrap.get', householdMissing: true }}
        onRetry={() => {
          void householdQ.refetch();
        }}
      />
    );
  }

  if (!isOnboardingComplete(household.onboardingStep)) {
    return <LoadingSpinner message="Taking you back to onboarding..." />;
  }

  if (
    !isDemo
    && subscriptionQ.isLoading
    && !subscriptionQ.data
  ) {
    return <LoadingSpinner message="Checking your access..." />;
  }

  // Single Tabs component — sidebar and tab bar visibility driven by isDesktop
  const demoBanner = demoBannerDismissed ? null : (
    <View className={isDesktop ? 'px-8 pt-4 max-w-[960px]' : 'px-4 pt-4'}>
      <DemoModeBanner
        demoAccountKey={household.demoAccountKey}
        compact
        onDismiss={() => setDemoBannerDismissed(true)}
        onTryAnotherDemo={() => {
          trackAuthSignedOut({
            route: pathname,
            source: 'demo_banner_try_another_demo',
            metadata: { isDemo: true },
          });
          if (Platform.OS === 'web') {
            clearSyntheticSessionForRedirect();
            window.location.replace('/?demo=1');
          } else {
            void supabase.auth.signOut().then(() => {
              router.replace('/(auth)/login');
            });
          }
        }}
        onSignUpNow={() => {
          trackAuthSignedOut({
            route: pathname,
            source: 'demo_banner_start_free_trial',
            metadata: { isDemo: true },
          });
          if (Platform.OS === 'web') {
            clearSyntheticSessionForRedirect();
            window.location.replace(buildSignupPath('web'));
          } else {
            void supabase.auth.signOut().then(() => {
              router.replace(buildSignupPath('router') as Href);
            });
          }
        }}
      />
    </View>
  );

  const tabs = (
    <Tabs
      screenOptions={{
        headerShown: false,
        lazy: true,
        tabBarStyle: isDesktop || shouldRestrictAppAccess
          ? { display: 'none' }
          : { backgroundColor: c.surface, borderTopColor: c.borderLight },
        tabBarActiveTintColor: c.primary,
        tabBarInactiveTintColor: c.textTertiary,
      }}
    >
      <Tabs.Screen
        name="index"
        redirect={shouldRestrictAppAccess}
        options={{
          title: 'Dashboard',
          tabBarLabel: 'Home',
          tabBarIcon: ({ color, size }) => <Ionicons name="home-outline" size={size} color={color} />,
        }}
      />
      <Tabs.Screen
        name="transactions"
        redirect={shouldRestrictAppAccess}
        options={{
          title: 'Transactions',
          tabBarLabel: 'Ledger',
          tabBarIcon: ({ color, size }) => <Ionicons name="receipt-outline" size={size} color={color} />,
          ...(visibleLedgerActionCount > 0 ? { href: ledgerActionHref } : {}),
          ...getTabActionBadgeOptions(visibleLedgerActionCount),
        }}
      />
      <Tabs.Screen
        name="accounts"
        redirect={shouldRestrictAppAccess}
        options={{
          title: 'Balance Sheet',
          tabBarLabel: 'Accounts',
          tabBarIcon: ({ color, size }) => <Ionicons name="wallet-outline" size={size} color={color} />,
        }}
      />
      <Tabs.Screen
        name="financial-statement"
        redirect={shouldRestrictAppAccess}
        options={{
          href: null,
          title: 'Personal Financial Statement',
        }}
      />
      <Tabs.Screen
        name="holdings"
        redirect={shouldRestrictAppAccess}
        options={{
          href: null,
          title: 'Portfolio',
        }}
      />
      <Tabs.Screen
        name="fire-plan"
        redirect={shouldRestrictAppAccess}
        options={{
          title: 'FIRE Plan',
          tabBarIcon: ({ color, size }) => <Ionicons name="flame-outline" size={size} color={color} />,
          ...getTabActionBadgeOptions(visibleFirePlanActionCount),
        }}
      />
      <Tabs.Screen
        name="scenarios"
        redirect={shouldRestrictAppAccess}
        options={{
          href: null,
          title: 'Scenarios',
        }}
      />
      <Tabs.Screen
        name="scenarios/[id]"
        redirect={shouldRestrictAppAccess}
        options={{
          href: null,
          title: 'Scenario',
        }}
      />
      <Tabs.Screen
        name="tax-strategy"
        redirect={shouldRestrictAppAccess}
        options={{
          href: null,
          title: 'Tax Strategy',
        }}
      />
      <Tabs.Screen
        name="tax-dashboard"
        redirect={shouldRestrictAppAccess}
        options={{
          href: null,
          title: 'Tax Dashboard',
        }}
      />
      <Tabs.Screen
        name="budget"
        redirect={shouldRestrictAppAccess}
        options={{
          title: 'Budget',
          tabBarIcon: ({ color, size }) => <Ionicons name="pie-chart-outline" size={size} color={color} />,
        }}
      />
      <Tabs.Screen
        name="budget-check-in"
        redirect={shouldRestrictAppAccess}
        options={{
          href: null,
          title: '90-Day Budget Check-In',
        }}
      />
      <Tabs.Screen
        name="ai-chat"
        redirect={shouldRestrictAppAccess}
        options={{
          title: 'AI Advisor',
          tabBarLabel: 'Ask AI',
          tabBarIcon: ({ color, size }) => <Ionicons name="sparkles-outline" size={size} color={color} />,
        }}
      />
      <Tabs.Screen
        name="category-rules"
        redirect={shouldRestrictAppAccess}
        options={{
          href: null,
          title: 'Category Rules',
        }}
      />
      <Tabs.Screen
        name="recurring"
        redirect={shouldRestrictAppAccess}
        options={{
          href: null,
          title: 'Recurring',
        }}
      />
      <Tabs.Screen
        name="integrations"
        redirect={shouldRestrictAppAccess}
        options={{
          href: null,
          title: 'Integrations',
        }}
      />
      <Tabs.Screen
        name="settings"
        options={{
          href: null,
          title: 'Settings',
        }}
      />
      <Tabs.Screen
        name="profile"
        redirect={shouldRestrictAppAccess}
        options={{
          href: null,
          title: 'Profile',
        }}
      />
    </Tabs>
  );

  if (isDesktop) {
    return (
      <View className="flex-1 flex-row bg-canvas dark:bg-canvas-dark">
        <MobileAppInterstitial
          isDemo={isDemo}
          isDesktop={isDesktop}
          pathname={pathname}
        />
        <WebSidebar
          isDemo={isDemo}
          accessLimited={shouldRestrictAppAccess}
          actionCounts={{
            ledger: visibleLedgerActionCount,
            firePlan: visibleFirePlanActionCount,
          }}
        />
        <View className="flex-1">
          {isDemo && demoBanner}
          {shouldShowSubscriptionExpiringBanner
          && expiringSubscriptionBannerKind ? (
            <SubscriptionExpiringBanner
              daysUntilExpiration={subscriptionQ.data?.daysUntilExpiration ?? null}
              kind={expiringSubscriptionBannerKind}
              onSubscribe={() => router.push('/(app)/settings')}
            />
          ) : null}
          {tabs}
          <FireRecalcBanner />
          <FireCelebrationHost enabled={!isDemo && !shouldRestrictAppAccess} />
        </View>
      </View>
    );
  }

  return (
    <SafeAreaView className="flex-1 bg-canvas dark:bg-canvas-dark" edges={['top', 'left', 'right']}>
      <View className="flex-1">
        <MobileAppInterstitial
          isDemo={isDemo}
          isDesktop={isDesktop}
          pathname={pathname}
        />
        <MobileTopBar accessLimited={shouldRestrictAppAccess} isDemo={isDemo} />
        {isDemo && demoBanner}
        {shouldShowSubscriptionExpiringBanner
        && expiringSubscriptionBannerKind ? (
          <SubscriptionExpiringBanner
            daysUntilExpiration={subscriptionQ.data?.daysUntilExpiration ?? null}
            kind={expiringSubscriptionBannerKind}
            onSubscribe={() => router.push('/(app)/settings')}
          />
        ) : null}
        {tabs}
        <FireRecalcBanner />
        <FireCelebrationHost enabled={!isDemo && !shouldRestrictAppAccess} />
      </View>
    </SafeAreaView>
  );
}
